import { Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { addMonths, endOfYear, format, isSameMonth, startOfYear } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { Employee } from 'src/app/models/Employee';
import { EmployeeContract } from 'src/app/models/EmployeeContract';
import EmployeeWithBudgets from 'src/app/models/EmployeeWithBudgets';
import { FleetVehicle, FleetVehicleHistory } from 'src/app/models/Fleet';
import { ModuleEnum } from 'src/app/models/ModuleEnum';
import Project from 'src/app/models/Project';
import { Timesheet } from 'src/app/models/Timesheet';
import { ModuleService } from 'src/app/services/module.service';
import { FetchTimesheetsForEmployee } from 'src/app/timesheets/timesheets.actions';
import { TimesheetState } from 'src/app/timesheets/timesheets.state';

import { AddBudgetModalComponent } from '../../budget/add-budget-modal/add-budget-modal.component';
import { ArchiveBudget } from '../../budget/budget.actions';
import Budget from '../../models/Budget';
import LinkedEmployee from '../../models/LinkedEmployee';
import { LinkedEmployeeViewModel } from '../../models/LinkedEmployeeViewModel';
import { StockUpdateHistoryModel } from '../../models/StockUpdateHistoryModel';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { GetAllFilesLinkedToObject } from '../../shared/global-files/global-files.actions';
import { LinkedInvoicesToObjectComponent } from '../../shared/global-files/linked-invoices-to-object/linked-invoices-to-object.component';
import { isPermitted } from '../../shared/utils/authorization-utils';
import { EmployeesArchiveModalComponent } from '../employees-archive-modal/employees-archive-modal.component';
import { AddEmployeeContractModalComponent } from '../add-employee-contract-modal/add-employee-contract-modal.component';
import { EmployeesDeleteModalComponent } from '../employees-delete-modal/employees-delete-modal.component';
import { EmployeesDetailModalComponent } from '../employees-detail-modal/employees-detail-modal.component';
import { EditDefaultLinkedEmployeeModalComponent } from '../../projects/edit-default-linked-employee-modal/edit-default-linked-employee-modal.component';
import { ProjectService } from '../../projects/project.service';
import { FleetService } from '../../fleet/fleet.service';
import {
    FetchBudgetsForEmployee,
    FetchLinkedEmployeesForEmployee,
    FetchProjectListByCurrentEmployee,
    FetchStockUpdateHistoryForEmployee,
    GetEmployeeById,
} from '../employees.actions';
import { EmployeeService } from '../employees.service';
import { EmployeeState } from '../employees.state';

@Component({
    selector: 'app-employees-detail',
    templateUrl: './employees-detail.component.html',
    styleUrls: ['./employees-detail.component.scss'],
})
export class EmployeesDetailComponent implements OnInit, OnDestroy {
    @Select(EmployeeState.BudgetsForEmployee) budgets$: Observable<Budget[]>;
    @Select(EmployeeState.StockUpdateHistoryForEmployee) stockUpdateForEmployee$: Observable<StockUpdateHistoryModel[]>;
    @Select(EmployeeState.CurrentEmployeeProjects) currentEmployeeProjects$: Observable<Project[]>;
    @Select(TimesheetState.timesheets) timesheets$: Observable<Timesheet[]>;
    timesheets: Timesheet[];
    @Select(EmployeeState.CurrentEmployee) currentEmployee$: Observable<Employee>;
    @Select(EmployeeState.LinkedObjectsToEmployee) linkedProducts$: Observable<LinkedEmployeeViewModel[]>;
    linkedProducts: LinkedEmployeeViewModel[];
    @Input() employeeUuid: string;
    @Input() openFinancialTab = false;
    selectedDate: Date;
    isLoading = true;
    timesheetsDataSource: MatTableDataSource<Timesheet> = new MatTableDataSource<Timesheet>();
    employee: Employee;
    eModule = ModuleEnum;
    budgets: Budget[] = [];
    selectedBudgetYear: number = new Date().getFullYear();
    linked: boolean = null;
    currentEmployeeWorksDataSource: MatTableDataSource<Project> = new MatTableDataSource<Project>();
    clientContractsDataSource: MatTableDataSource<EmployeeContractRow> = new MatTableDataSource<EmployeeContractRow>();
    employeeContractsDataSource: MatTableDataSource<EmployeeContract> = new MatTableDataSource<EmployeeContract>();
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    employeeStockUpdates: MatTableDataSource<StockUpdateHistoryModel>;
    linkedEmployeeProducts: LinkedEmployee[];
    currentLinkedProductsUuid: string[] = [];
    fleetLoading = false;
    currentFleetVehicle: FleetVehicle = null;
    fleetHistory: FleetVehicleHistory[] = [];
    displayedColumnsTimesheet: string[] = ['timesheet', 'timeslot', 'totalHours'];
    displayedColumns: string[] = ['updatedProductTitle', 'reasonInfo', 'dateAmTimeAgo', 'date', 'stockChange'];
    displayedColumnsForProjects: string[] = ['title', 'status', 'startDate', 'endDate', 'description'];
    displayedColumnsForClientContracts: string[] = [
        'project',
        'price per hour',
        'effective from',
        'effective to',
        'po number',
        'ses number',
        'contract number',
        'free text',
        'contract',
        'edit',
    ];
    displayedColumnsForEmployeeContracts: string[] = [
        'price per hour',
        'effective from',
        'effective to',
        'po number',
        'ses number',
        'contract number',
        'free text',
        'contract',
        'edit',
    ];
    startDateFilter: string = format(startOfYear(new Date()), 'yyyy-MM-dd');
    endDateFilter: string = format(endOfYear(new Date()), 'yyyy-MM-dd');
    @ViewChildren(LinkedInvoicesToObjectComponent) linkedInvoicesToObjectComponents: QueryList<LinkedInvoicesToObjectComponent>;
    budgetsLoaded = false;
    calculateActualCostForEmployee = EmployeeWithBudgets.calculateActualCostForEmployee;
    calculateBudgetCostForEmployee = EmployeeWithBudgets.calculateBudgetCostForEmployee;

    activeNavTab = 'information';

    private subscriptions = new Subscription();

    constructor(
        public activeModal: NgbActiveModal,
        private modalService: NgbModal,
        private store: Store,
        public moduleService: ModuleService,
        private logger: NGXLogger,
        private employeeService: EmployeeService,
        private fleetService: FleetService,
        private projectService: ProjectService,
        private toastr: ToastrService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        if (this.openFinancialTab) this.activeNavTab = 'financial';

        if (!this.openFinancialTab) {
            this.logger.debug('Fetching all files linked to object');
            this.store.dispatch(new GetAllFilesLinkedToObject(this.employeeUuid));
        }

        this.selectedDate = addMonths(new Date(), -1);

        this.logger.debug('Fetching employee by id');
        this.store.dispatch(new GetEmployeeById(this.employeeUuid));

        this.subscriptions.add(
            this.currentEmployee$.subscribe((employee) => {
                this.employee = employee;
                this.logger.debug('Fetching project for employee');
                if (employee) {
                    this.subscriptions.add(
                        this.employeeService.checkIfUserIsLinked(employee.uuid).subscribe((res) => {
                            this.linked = res;
                        }),
                    );

                    if (this.moduleService.checkModuleActive(this.eModule.FLEET)) {
                        this.fetchEmployeeFleet();
                    }

                    this.isLoading = false;
                }
            }),
        );

        this.store.dispatch(new FetchProjectListByCurrentEmployee(this.employeeUuid));
        this.subscriptions.add(
            this.currentEmployeeProjects$.subscribe((projects) => {
                const sortedProjects = this.sortProjectsByDate(projects || []);
                this.currentEmployeeWorksDataSource = new MatTableDataSource<Project>(sortedProjects);
                this.clientContractsDataSource = new MatTableDataSource<EmployeeContractRow>(this.mapEmployeeContracts(sortedProjects));
            }),
        );

        this.refreshEmployeeContracts();

        this.store.dispatch(new FetchStockUpdateHistoryForEmployee(this.employeeUuid));
        this.subscriptions.add(
            this.stockUpdateForEmployee$.subscribe((stockHistoryUpdate) => {
                if (stockHistoryUpdate) {
                    this.employeeStockUpdates = new MatTableDataSource(stockHistoryUpdate);
                    this.employeeStockUpdates.sort = this.sort;
                    this.employeeStockUpdates.filterPredicate = (data, filter) => {
                        return data.employeeUuid === this.employeeUuid;
                    };
                    this.employeeStockUpdates.filter = JSON.stringify({
                        employeeUuid: this.employeeUuid,
                    });
                }
            }),
        );

        // TODO: create enum for types
        this.store.dispatch(new FetchLinkedEmployeesForEmployee(this.employeeUuid, 'product'));
        this.subscriptions.add(
            this.linkedProducts$.subscribe((linkedProducts) => {
                if (!linkedProducts) return;

                this.linkedProducts = linkedProducts;
            }),
        );
        if (isPermitted({ module: 'BUDGET', operator: 'AND', actions: ['READ'] })) {
            this.logger.debug('Fetching budgets for employee');
            this.store.dispatch(new FetchBudgetsForEmployee(this.employeeUuid));
        }

        this.subscriptions.add(
            this.budgets$.subscribe((budgets) => {
                this.budgets = budgets || [];

                const availableYears = this.budgetYears;
                const currentYear = new Date().getFullYear();
                if (availableYears.includes(currentYear)) {
                    this.selectedBudgetYear = currentYear;
                } else if (availableYears.length > 0 && !availableYears.includes(this.selectedBudgetYear)) {
                    this.selectedBudgetYear = availableYears[0];
                }
            }),
        );

        this.logger.debug('Fetching timesheets for employee');
        this.store.dispatch(new FetchTimesheetsForEmployee(this.employeeUuid));
        this.subscriptions.add(
            this.timesheets$.subscribe((timesheets) => {
                this.timesheets = timesheets;
                this.timesheetsDataSource.data = timesheets;

                this.timesheetsDataSource.filterPredicate = (data, filter) => {
                    const employeeTimesheetFilter: EmployeeTimesheetFilter = JSON.parse(filter);
                    return (
                        isSameMonth(new Date(data.expirationDate), new Date(employeeTimesheetFilter.date)) &&
                        employeeTimesheetFilter.employeeId === data.employee.uuid
                    );
                };
                this.filterTimesheetsForEmployee();
            }),
        );
    }

    openEmployeeUpdateModal(employee: Employee) {
        this.logger.debug('Opening employeesDetailModal - update');
        const modalRef = this.modalService.open(EmployeesDetailModalComponent);
        modalRef.componentInstance.employeeUuid = employee.uuid;
        modalRef.componentInstance.inOverview = false;
    }

    openEmployeeDeleteModal(employeeUuid: string) {
        this.logger.debug('Opening employeesDeleteModal');
        const modalRef = this.modalService.open(EmployeesDeleteModalComponent, {
            windowClass: 'modal-prompt',
        });
        modalRef.componentInstance.employee = this.employee;
        this.subscriptions.add(modalRef.componentInstance.closeEvent.subscribe(() => this.activeModal.dismiss()));
    }

    openEmployeeArchiveModal(employee: Employee) {
        this.logger.debug('Opening employeesArchiveModal');
        const modalRef = this.modalService.open(EmployeesArchiveModalComponent, { windowClass: 'modal-prompt' });
        modalRef.componentInstance.employee = employee;
        this.subscriptions.add(modalRef.componentInstance.closeEvent.subscribe(() => this.activeModal.dismiss()));
    }

    openEmployeeDetail() {
        this.logger.debug('Opening employeesDetail');
        const modalRef = this.modalService.open(EmployeesDetailComponent, {
            windowClass: 'modal-prompt',
        });
        modalRef.componentInstance.employee = this.employee;
        modalRef.componentInstance.inOverview = false;
    }

    updateCurrentLinkedProducts() {
        this.currentLinkedProductsUuid = [];
        this.linkedEmployeeProducts.forEach((product) => {
            product.employees.forEach((employee) => {
                if (employee.uuid === this.employee.uuid) {
                    this.currentLinkedProductsUuid.push(product.uuid);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    fetchEmployeeFleet(): void {
        this.fleetLoading = true;
        this.subscriptions.add(
            this.fleetService.getEmployeeFleet(this.employeeUuid).subscribe({
                next: (fleetData) => {
                    this.currentFleetVehicle = fleetData?.currentVehicle || null;
                    this.fleetHistory = fleetData?.history || [];
                    this.fleetLoading = false;
                },
                error: () => {
                    this.currentFleetVehicle = null;
                    this.fleetHistory = [];
                    this.fleetLoading = false;
                },
            }),
        );
    }

    deleteBudget(budget: Budget) {
        this.logger.debug('Opening confirmationModal');
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        if (budget.archived) {
            modalRef.componentInstance.type = 'YesOrNo';
            modalRef.componentInstance.title = 'Unarchive budget';
            modalRef.componentInstance.htmlMessage = `<p>Are you sure you want to unarchive <strong>${budget.budgetType}</strong>?</p>`;
        } else {
            modalRef.componentInstance.type = 'Archive';
            modalRef.componentInstance.title = 'Archive budget';
            modalRef.componentInstance.htmlMessage = `<p>Are you sure you want to archive <strong>${budget.budgetType}</strong>?</p>`;
        }
        this.subscriptions.add(
            modalRef.componentInstance.closeEvent.subscribe((value) => {
                if (value) {
                    this.logger.debug('Attempt to (un)archive Budget');
                    this.store.dispatch(new ArchiveBudget(budget.uuid)).subscribe(
                        (res) => {
                            this.logger.debug('Successfully (un)archived budget');
                            if (budget.archived) {
                                this.toastr.success('Budget successfully unarchived.');
                            } else {
                                this.toastr.success('Budget successfully archived.');
                            }
                        },
                        (err) => {
                            this.logger.debug('Failed to (un)archive budget');
                            this.toastr.error('Oops, something went wrong, Please try again later...');
                        },
                    );
                }
            }),
        );
    }

    openUpdateBudgetModal(budget) {
        const modalRef = this.modalService.open(AddBudgetModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.budget = budget;
        modalRef.componentInstance.type = 'Update budget';
        modalRef.componentInstance.linkedEmployee = this.employee;
    }

    openAddBudgetModal() {
        this.logger.debug('Opening addBudgetModal');
        const modalRef = this.modalService.open(AddBudgetModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.type = 'Assign budget';
        modalRef.componentInstance.linkedEmployee = this.employee;
    }

    sortData(sort: Sort) {
        this.employeeStockUpdates.sort = this.sort;
    }

    sortProjectsData(sort: Sort): void {
        const data = this.currentEmployeeWorksDataSource?.data || [];
        if (!sort.active || sort.direction === '') {
            this.currentEmployeeWorksDataSource.data = this.sortProjectsByDate(data);
            return;
        }

        const isAsc = sort.direction === 'asc';
        this.currentEmployeeWorksDataSource.data = [...data].sort((a, b) => {
            switch (sort.active) {
                case 'title':
                    return this.compare((a.title || '').toLowerCase(), (b.title || '').toLowerCase(), isAsc);
                case 'status':
                    return this.compare(this.getProjectStatus(a), this.getProjectStatus(b), isAsc);
                case 'startDate':
                    return this.compare(this.toDateValue(a.startDate), this.toDateValue(b.startDate), isAsc);
                case 'endDate':
                    return this.compare(this.toDateValue(a.endDate), this.toDateValue(b.endDate), isAsc);
                case 'description':
                    return this.compare((a.description || '').toLowerCase(), (b.description || '').toLowerCase(), isAsc);
                default:
                    return 0;
            }
        });
    }

    getProjectStatus(project: Project): string {
        if (project?.archived) {
            return 'Archived';
        }
        if (project?.finished) {
            return 'Finished';
        }
        return 'Active';
    }

    private sortProjectsByDate(projects: Project[]): Project[] {
        return [...(projects || [])].sort((a, b) => {
            const aDate = this.toDateValue(a.startDate) || this.toDateValue(a.endDate);
            const bDate = this.toDateValue(b.startDate) || this.toDateValue(b.endDate);
            return bDate - aDate;
        });
    }

    private toDateValue(date?: string): number {
        if (!date) {
            return 0;
        }
        const time = new Date(date).getTime();
        return Number.isNaN(time) ? 0 : time;
    }

    private compare(a: string | number, b: string | number, isAsc: boolean): number {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    getTotalMinutes(): number {
        let totalMinutes = 0;
        this.timesheetsDataSource.filteredData.forEach((ts) => {
            totalMinutes += this.getTotalMinutesForTimesheet(ts);
        });
        return totalMinutes;
    }

    moveDate(amount: number) {
        if (amount === 0) {
            this.selectedDate = addMonths(new Date(), -1);
        } else {
            this.selectedDate = addMonths(this.selectedDate, amount);
        }
        this.filterTimesheetsForEmployee();
    }

    calculateFirstDayOfGivenMonth(startDate: Date): Date {
        return new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    }

    filterTimesheetsForEmployee() {
        const filter: EmployeeTimesheetFilter = {
            employeeId: this.employeeUuid,
            date: this.selectedDate.toString(),
        };
        this.timesheetsDataSource.filter = JSON.stringify(filter);
    }

    getTotalMinutesForTimesheet(timesheet: Timesheet): number {
        let totalMinutes = 0;
        timesheet.timeslots.forEach((el) => {
            totalMinutes += el.minutes;
        });
        return totalMinutes;
    }

    getRequiredHoursDiff(timesheet: Timesheet) {
        return Timesheet.getRequiredHoursDiff(timesheet);
    }

    getRequiredHoursDiffPercentage(timesheet: Timesheet) {
        return Timesheet.getRequiredHoursDiffPercentage(timesheet);
    }

    navigateToTimesheets(): void {
        this.logger.debug('Navigating to timesheets');
        this.activeModal.dismiss();
        this.router.navigate(['/timesheets']);
    }

    createHtmlSubtitleForNoTimesheets(): string {
        return `No timesheets for <strong>${this.employee.firstName} ${this.employee.lastName}</strong>`;
    }

    filterDate() {
        this.linkedInvoicesToObjectComponents.forEach((linkedInvoicesToObjectComponent) =>
            linkedInvoicesToObjectComponent.filterOnDate(new Date(this.startDateFilter), new Date(this.endDateFilter)),
        );
    }

    private mapEmployeeContracts(projects: Project[]): EmployeeContractRow[] {
        return (projects || [])
            .reduce((result, project) => {
                const links = (project.defaultLinkedEmployees || [])
                    .filter((link) => link.employeeId === this.employeeUuid)
                    .map((link) => ({
                        ...link,
                        project,
                    }));

                return [...result, ...links];
            }, [] as EmployeeContractRow[]);
    }

    private refreshEmployeeProjects(): void {
        this.store.dispatch(new FetchProjectListByCurrentEmployee(this.employeeUuid));
    }

    private refreshEmployeeContracts(): void {
        this.subscriptions.add(
            this.employeeService.getEmployeeContracts(this.employeeUuid).subscribe(
                (contracts) => {
                    this.employeeContractsDataSource = new MatTableDataSource<EmployeeContract>(contracts || []);
                },
                () => {
                    this.employeeContractsDataSource = new MatTableDataSource<EmployeeContract>([]);
                },
            ),
        );
    }

    private refreshContracts(): void {
        this.refreshEmployeeProjects();
        this.refreshEmployeeContracts();
    }

    openAddContractModal(scope: 'CLIENT' | 'EMPLOYEE'): void {
        const modalRef = this.modalService.open(AddEmployeeContractModalComponent, {
            windowClass: 'modal-prompt',
            size: 'xl',
            animation: false,
        });
        modalRef.componentInstance.employee = this.employee;
        modalRef.componentInstance.scope = scope;
        this.subscriptions.add(modalRef.closed.subscribe(() => this.refreshContracts()));
    }

    openEditContractModal(contract: EmployeeContractRow): void {
        if (!contract?.uuid) {
            this.toastr.error('Contract id ontbreekt.');
            return;
        }
        const modalRef = this.modalService.open(EditDefaultLinkedEmployeeModalComponent, {
            windowClass: 'modal-prompt',
            size: 'xl',
            animation: false,
        });
        const linkedEmployee: LinkedEmployee = {
            uuid: contract.uuid,
            employeeId: this.employeeUuid,
            pricePerHour: contract.pricePerHour,
            effectiveFrom: contract.effectiveFrom,
            effectiveTo: contract.effectiveTo,
            hasClientContract: contract.hasClientContract,
            clientContractUrl: contract.clientContractUrl,
            clientContractFileName: contract.clientContractFileName,
            hasEmployeeContract: contract.hasEmployeeContract,
            employeeContractUrl: contract.employeeContractUrl,
            employeeContractFileName: contract.employeeContractFileName,
            poNumber: contract.poNumber,
            sesNumber: contract.sesNumber,
            contractAgreementNumber: contract.contractAgreementNumber,
            freeText: contract.freeText,
        };
        modalRef.componentInstance.linkedEmployee = linkedEmployee;
        this.subscriptions.add(modalRef.closed.subscribe(() => this.refreshContracts()));
    }

    deleteContract(contract: EmployeeContractRow): void {
        if (!contract?.uuid) return;
        this.subscriptions.add(
            this.projectService.deleteDefaultEmployee(contract.uuid).subscribe(
                () => {
                    this.toastr.success('Contract verwijderd.');
                    this.refreshContracts();
                },
                () => {
                    this.toastr.error('Verwijderen contract mislukt.');
                },
            ),
        );
    }

    deleteEmployeeContract(contract: EmployeeContract): void {
        if (!contract?.uuid) return;
        this.subscriptions.add(
            this.employeeService.deleteEmployeeContract(contract.uuid).subscribe(
                () => {
                    this.toastr.success('Contract verwijderd.');
                    this.refreshContracts();
                },
                () => {
                    this.toastr.error('Verwijderen contract mislukt.');
                },
            ),
        );
    }

    /**
     * Convert minutes to days (1 day = 8 hours = 480 minutes)
     */
    minutesToDays(minutes: number): number {
        return Math.floor(minutes / 480);
    }

    /**
     * Get formatted string with days (e.g., "10 days")
     */
    getDaysLabel(minutes: number): string {
        const days = this.minutesToDays(minutes);
        if (days === 0) {
            return '';
        }
        return `(${days} ${days === 1 ? 'day' : 'days'})`;
    }

    get budgetYears(): number[] {
        const years: number[] = [];
        (this.budgets || []).forEach((budget) => {
            years.push(...this.getBudgetCoveredYears(budget));
        });

        return Array.from(new Set(years)).sort((a, b) => b - a);
    }

    get budgetsForSelectedYear(): Budget[] {
        const selectedYear = Number(this.selectedBudgetYear);
        return (this.budgets || []).filter((budget) => this.isBudgetInYear(budget, selectedYear));
    }

    get visibleBudgetsTotalAmount(): number {
        return (this.budgetsForSelectedYear || []).reduce((total, budget) => total + (Number(budget?.totalSpend) || 0), 0);
    }

    private getBudgetCoveredYears(budget: Budget): number[] {
        const startYear = this.getBudgetStartYear(budget);
        const endYear = this.getBudgetEndYear(budget);
        if (startYear === null || endYear === null) {
            return [];
        }

        const years: number[] = [];
        for (let year = startYear; year <= endYear; year++) {
            years.push(year);
        }
        return years;
    }

    private isBudgetInYear(budget: Budget, year: number): boolean {
        const startYear = this.getBudgetStartYear(budget);
        const endYear = this.getBudgetEndYear(budget);
        if (startYear === null || endYear === null) {
            return false;
        }
        return startYear <= year && endYear >= year;
    }

    private getBudgetStartYear(budget: Budget): number | null {
        return this.getYearFromDateString(budget?.startDate || budget?.endDate);
    }

    private getBudgetEndYear(budget: Budget): number | null {
        return this.getYearFromDateString(budget?.endDate || budget?.startDate);
    }

    private getYearFromDateString(value: string): number | null {
        if (!value) {
            return null;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        return date.getFullYear();
    }

}

export interface EmployeeTimesheetFilter {
    date: string;
    employeeId: string;
}

interface EmployeeContractRow extends Partial<LinkedEmployee> {
    project: Project;
}
